/**
 * useAutoReconcile — state + side-effects for the Auto Reconcile dialog.
 *
 * Owns the upload/review state machine so the dialog view stays declarative.
 *
 * State buckets in the Review phase:
 *   - selectedIds: transaction IDs the user kept ticked from the auto-match list.
 *   - importIds:   synthetic IDs of unmatched statement rows the user marked
 *                  for "+ Add to Forge Ledger". Each becomes a new reconciled
 *                  transaction in the chosen account.
 *   - pairTags:    Map<key, string>. Keys are either ``stmt::<id>`` or
 *                  ``txn::<transaction_id>``. Values are the user-typed
 *                  pair number (e.g. "1", "2"). Same number on a stmt + txn
 *                  row = a manual pair; on Apply the txn is overwritten with
 *                  the statement's amount/date and marked reconciled.
 *
 * Statement rows have no stable ID coming back from the backend, so we mint a
 * synthetic stable key from (date|description|amount). That's good enough
 * because the auto-matcher won't return duplicate identical rows.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/** Stable per-row identifier for an unmatched statement row. */
export const stmtKey = (row) => `${row.date}|${row.description || ''}|${row.amount}`;

export default function useAutoReconcile({ onApplied, onClose }) {
  const [file, setFile] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [matching, setMatching] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importIds, setImportIds] = useState(new Set());
  const [pairTags, setPairTags] = useState({});
  const [applying, setApplying] = useState(false);
  // After a successful apply we hand off to the Success phase (celebration
  // + social share prompt) instead of immediately closing. Null while the
  // user is still in upload/review.
  const [successStats, setSuccessStats] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setAccountId('');
    setMatching(false);
    setResult(null);
    setSelectedIds(new Set());
    setImportIds(new Set());
    setPairTags({});
    setApplying(false);
    setSuccessStats(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * User clicked "Done" on the success screen — refresh the parent's
   * data, close the dialog, and reset our state.
   */
  const dismissSuccess = () => {
    if (onApplied) onApplied();
    if (onClose) onClose();
    reset();
  };

  const runMatch = async () => {
    if (!file || !accountId) {
      toast.error('Pick an account and a statement file first.');
      return;
    }
    setMatching(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/reconcile/match?account_id=${accountId}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(r.data);
      // Pre-select every confident match. User can deselect false positives.
      setSelectedIds(new Set(r.data.matched.map((m) => m.transaction.transaction_id)));
      setImportIds(new Set());
      setPairTags({});
    } catch (err) {
      toast.error(errorMessage(err, 'Matching failed — check the file format.'));
    } finally {
      setMatching(false);
    }
  };

  const toggleMatch = (txnId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId); else next.add(txnId);
      return next;
    });
  };

  const toggleImport = (key) => {
    setImportIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    // If the user has tagged this row for a pair AND marked it for import,
    // the pair wins — clear the import flag silently.
    setPairTags((prev) => {
      const tag = prev[`stmt::${key}`];
      if (!tag) return prev;
      const copy = { ...prev };
      // No-op; allow both, but pair takes precedence on Apply (see below).
      return copy;
    });
  };

  const setPairTag = (kind, id, tag) => {
    // Normalise: empty / non-digit input clears the tag.
    const cleaned = (tag || '').replace(/[^0-9]/g, '').slice(0, 2);
    setPairTags((prev) => {
      const key = `${kind}::${id}`;
      const next = { ...prev };
      if (!cleaned) {
        delete next[key];
      } else {
        next[key] = cleaned;
      }
      return next;
    });
  };

  /** Derive the {stmt, txn} pairs from pairTags. Only tags that exist on
   *  BOTH a statement row and an app transaction become real pairs. */
  const derivePairs = () => {
    if (!result) return [];
    const byTag = new Map(); // tag -> { stmt?: row, txn?: txnObj }
    Object.entries(pairTags).forEach(([key, tag]) => {
      const [kind, id] = key.split('::');
      const bucket = byTag.get(tag) || {};
      if (kind === 'stmt') {
        bucket.stmtKey = id;
      } else if (kind === 'txn') {
        bucket.txnId = id;
      }
      byTag.set(tag, bucket);
    });

    const pairs = [];
    byTag.forEach((bucket) => {
      if (bucket.stmtKey && bucket.txnId) {
        const stmt = (result.unmatched_statement || []).find(
          (r) => stmtKey(r) === bucket.stmtKey,
        );
        if (stmt) {
          pairs.push({
            transaction_id: bucket.txnId,
            statement: {
              date: stmt.date,
              description: stmt.description || '',
              amount: stmt.amount,
            },
          });
        }
      }
    });
    return pairs;
  };

  const applyMatches = async () => {
    const manualPairs = derivePairs();
    // A row marked for import that's ALSO in a manual pair should be paired,
    // not imported. Strip those from importIds before sending.
    const pairedStmtKeys = new Set(
      manualPairs.map((p) => stmtKey({
        date: p.statement.date,
        description: p.statement.description,
        amount: p.statement.amount,
      })),
    );
    const importRows = (result?.unmatched_statement || [])
      .filter((r) => importIds.has(stmtKey(r)) && !pairedStmtKeys.has(stmtKey(r)));

    const totalActions = selectedIds.size + manualPairs.length + importRows.length;
    if (totalActions === 0) {
      toast.error('Tick a match, tag a pair, or pick a row to import first.');
      return;
    }

    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/reconcile/apply`,
        {
          transaction_ids: Array.from(selectedIds),
          account_id: accountId,
          import_rows: importRows.map((row) => ({
            date: row.date,
            description: row.description || '',
            amount: row.amount,
          })),
          manual_pairs: manualPairs,
        },
        { withCredentials: true },
      );
      const { reconciled = 0, paired = 0, imported = 0 } = r.data || {};
      const parts = [];
      if (reconciled) parts.push(`${reconciled} reconciled`);
      if (paired) parts.push(`${paired} paired`);
      if (imported) parts.push(`${imported} imported`);
      // Only celebrate when SOMETHING actually changed — a zero-action
      // apply (which shouldn't be reachable but belt-and-braces) just
      // shows the toast and closes silently.
      if (reconciled + paired + imported === 0) {
        toast.success('Nothing to apply.');
        if (onApplied) onApplied();
        if (onClose) onClose();
        reset();
        return;
      }
      toast.success(parts.join(' · '));
      // Hand off to the success/share phase. The parent's data refetch
      // (onApplied) and modal close happen when the user clicks "Done".
      setSuccessStats({ reconciled, paired, imported });
    } catch (err) {
      toast.error(errorMessage(err, 'Apply failed — please try again.'));
    } finally {
      setApplying(false);
    }
  };

  return {
    file, setFile,
    accountId, setAccountId,
    matching, applying,
    result, selectedIds, importIds, pairTags,
    successStats,
    fileInputRef,
    runMatch, toggleMatch, toggleImport, setPairTag,
    applyMatches, reset, dismissSuccess,
  };
}
